import { useAuthStore } from '../store/auth';

// В production используем относительный путь (nginx проксирует на backend)
// В development можно использовать VITE_API_URL, иначе всегда /api
const API_URL = import.meta.env.DEV 
  ? (import.meta.env.VITE_API_URL || 'http://localhost:3002/api')
  : '/api';

class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Берём initData из store или напрямую из Telegram WebApp
  let initData = useAuthStore.getState().initData;
  if (!initData && typeof window !== 'undefined') {
    initData = window.Telegram?.WebApp?.initData || '';
    if (initData) {
      useAuthStore.getState().setInitData(initData);
    }
  }
  if (!initData) {
    const maxWait = 5000;
    const checkInterval = 50;
    const startTime = Date.now();

    while (!initData && (Date.now() - startTime) < maxWait) {
      await new Promise(resolve => setTimeout(resolve, checkInterval));
      initData = useAuthStore.getState().initData || window.Telegram?.WebApp?.initData || '';
      if (initData) {
        useAuthStore.getState().setInitData(initData);
      }
    }
  }

  if (!initData && window.Telegram?.WebApp) {
    throw new ApiError(401, 'Не удалось получить данные авторизации Telegram. Закройте приложение и откройте его через бота.');
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(initData && { Authorization: `tma ${initData}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorMessage = 'Неизвестная ошибка';
    try {
      const error = await response.json();
      errorMessage = error.error || error.message || `Ошибка ${response.status}`;
    } catch (parseError) {
      // Если ответ не JSON, используем стандартное сообщение по статусу
      if (response.status === 401) {
        errorMessage = 'Требуется авторизация';
      } else if (response.status === 403) {
        errorMessage = 'Доступ запрещен';
      } else if (response.status === 404) {
        errorMessage = 'Не найдено';
      } else if (response.status === 409) {
        errorMessage = 'Конфликт: нельзя выполнить операцию';
      } else if (response.status === 500) {
        errorMessage = 'Ошибка сервера';
      } else {
        errorMessage = `Ошибка ${response.status}`;
      }
      console.error(`[API] Failed to parse error response for ${endpoint}:`, parseError);
    }
    
    // Логируем ошибку для отладки
    const errorDetails = errorMessage.includes('error') ? await response.text().catch(() => null) : null;
    console.error(`[API] Request failed: ${endpoint}`, {
      status: response.status,
      statusText: response.statusText,
      message: errorMessage,
      hasInitData: !!initData,
      errorDetails,
      method: options.method || 'GET',
    });
    
    const apiError = new ApiError(response.status, errorMessage);
    (apiError as any).errorDetails = errorDetails;
    throw apiError;
  }

  // Для статуса 204 (No Content) не пытаемся парсить JSON
  if (response.status === 204) {
    return undefined as T;
  }

  // Проверяем, есть ли тело ответа
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  // Если нет JSON, возвращаем пустой объект
  return {} as T;
}

// ==================== PROJECTS ====================

export interface ProjectMember {
  id: string;
  visibleId?: string;
  telegramId: string | null;
  firstName: string;
  lastName: string | null;
  username: string | null;
  photoUrl: string | null;
  role: 'ADMIN' | 'MANAGER' | 'EXECUTOR' | 'OBSERVER';
  position: string | null;
}

export interface Project {
  id: string;
  name: string;
  description: string | null;
  googleSheetId: string | null;
  kpiSplitMode: 'EACH_FULL' | 'SPLIT_EQUAL';
  role: 'ADMIN' | 'MANAGER' | 'EXECUTOR' | 'OBSERVER';
  protocolsCount: number;
  members?: ProjectMember[];
  createdAt: string;
  updatedAt: string;
}

export const projectsApi = {
  list: () => request<Project[]>('/projects'),
  
  get: (id: string) => request<Project>(`/projects/${id}`),
  
  create: (data: { name: string; description?: string; googleSheetId?: string }) =>
    request<Project>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: Partial<Project>) =>
    request<Project>(`/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) =>
    request<{ success: boolean }>(`/projects/${id}`, {
      method: 'DELETE',
    }),
  
  // Управление участниками
  addMember: (projectId: string, data: { 
    username?: string; 
    telegramId?: string; 
    firstName?: string;
    lastName?: string;
    position?: string;
    role: string;
  }) =>
    request<ProjectMember>(`/projects/${projectId}/members`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateMemberRole: (projectId: string, userId: string, role: string) =>
    request<any>(`/projects/${projectId}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
  
  updateMember: (projectId: string, userId: string, data: {
    firstName?: string;
    lastName?: string;
    username?: string;
    position?: string;
    role?: string;
  }) =>
    request<ProjectMember>(`/projects/${projectId}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  removeMember: (projectId: string, userId: string) =>
    request<{ success: boolean }>(`/projects/${projectId}/members/${userId}`, {
      method: 'DELETE',
    }),
};

// ==================== PROTOCOLS ====================

export interface Protocol {
  id: string;
  projectId: string;
  title: string;
  level: 'TOP' | 'BOTTOM';
  meetingDate: string;
  theme: string | null;
  chairman: string | null;
  attendees: string[];
  sectionsCount: number;
  tasksCount: number;
  createdBy: {
    id: string;
    firstName: string;
    lastName: string | null;
  };
  createdAt: string;
}

export interface ProtocolDetail extends Protocol {
  userRole: 'ADMIN' | 'MANAGER' | 'EXECUTOR' | 'OBSERVER';
  sections: Section[];
}

export interface Section {
  id: string;
  number: number;
  title: string;
  sortOrder: number;
  tasks: Task[];
}

export const sheetsApi = {
  getServiceAccountEmail: () =>
    request<{ serviceAccountEmail: string }>('/sheets/service-account-email'),
  
  sync: (projectId: string, direction: 'to_sheets' | 'from_sheets') =>
    request<{ success: boolean; message: string; tasksCount?: number }>('/sheets/sync', {
      method: 'POST',
      body: JSON.stringify({ projectId, direction }),
    }),
};

export const protocolsApi = {
  list: (projectId: string) =>
    request<Protocol[]>(`/protocols?projectId=${projectId}`),
  
  listMy: () =>
    request<Array<Protocol & { projectName: string }>>('/protocols/my'),
  
  get: (id: string) => request<ProtocolDetail>(`/protocols/${id}`),
  
  create: (data: {
    projectId: string;
    title: string;
    level?: 'TOP' | 'BOTTOM';
    meetingDate: string;
    theme?: string;
    chairman?: string;
    attendees?: string[];
    templateId?: string;
  }) =>
    request<Protocol>('/protocols', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: Partial<Protocol>) =>
    request<Protocol>(`/protocols/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  export: (id: string) =>
    request<{ success: boolean; message: string; spreadsheetUrl: string }>(`/protocols/${id}/export`, {
      method: 'POST',
    }),
  
  sync: (id: string) =>
    request<{ 
      success: boolean; 
      message: string; 
      stats: { total: number; updated: number; skipped: number; errors?: string[] } 
    }>(`/protocols/${id}/sync`, {
      method: 'POST',
    }),
};

// ==================== SECTIONS ====================

export const sectionsApi = {
  create: (data: { protocolId: string; number: number; title: string }) =>
    request<Section>('/sections', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: { title?: string; sortOrder?: number }) =>
    request<Section>(`/sections/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) =>
    request<{ success: boolean }>(`/sections/${id}`, {
      method: 'DELETE',
    }),
  
  initDefault: (protocolId: string) =>
    request<Section[]>('/sections/init-default', {
      method: 'POST',
      body: JSON.stringify({ protocolId }),
    }),
  
  reorder: (sections: Array<{ id: string; sortOrder: number }>) =>
    request<{ success: boolean }>('/sections/reorder', {
      method: 'POST',
      body: JSON.stringify({ sections }),
    }),
};

// ==================== TEMPLATES ====================

export interface ProtocolTemplate {
  id: string;
  name: string;
  level: 'TOP' | 'BOTTOM';
  theme?: string | null;
  chairman?: string | null;
  sectionsCount: number;
  sections?: Array<{
    id: string;
    number: number;
    title: string;
    sortOrder: number;
  }>;
  createdBy: {
    id: string;
    firstName: string;
    lastName: string | null;
  };
  createdAt: string;
}

export const templatesApi = {
  list: (projectId: string) =>
    request<ProtocolTemplate[]>(`/templates?projectId=${projectId}`),
  
  get: (id: string) => request<ProtocolTemplate>(`/templates/${id}`),
  
  create: (data: {
    projectId: string;
    name: string;
    level?: 'TOP' | 'BOTTOM';
    theme?: string;
    chairman?: string;
    sections?: Array<{ number: number; title: string }>;
  }) =>
    request<ProtocolTemplate>('/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: Partial<ProtocolTemplate>) =>
    request<ProtocolTemplate>(`/templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) =>
    request<{ success: boolean }>(`/templates/${id}`, {
      method: 'DELETE',
    }),
};

// ==================== TASKS ====================

export interface Task {
  id: string;
  code: string;
  text: string;
  startDate: string | null;
  dueDate: string | null;
  closedAt: string | null;
  priority: 'CRITICAL' | 'HIGH' | 'CURRENT';
  status: 'CREATED' | 'IN_PROGRESS' | 'PENDING_APPROVAL' | 'COMPLETED' | 'PAUSED' | 'FAILED' | 'NOT_SELECTED';
  assignees: Assignee[];
  recentComments?: Comment[];
  commentsCount?: number;
  sectionNumber?: number;
  sectionTitle?: string;
  section?: {
    protocol?: {
      id: string;
      title: string;
      project?: {
        id: string;
        name: string;
      };
    };
  };
  createdAt: string;
}

export interface TaskDetail extends Omit<Task, 'section'> {
  section: {
    id: string;
    number: number;
    title: string;
    protocol?: {
      id: string;
      title: string;
      project?: {
        id: string;
        name: string;
      };
    };
  };
  comments: Comment[];
  createdBy: {
    id: string;
    firstName: string;
    lastName: string | null;
  };
  userRole: 'ADMIN' | 'MANAGER' | 'EXECUTOR' | 'OBSERVER';
  updatedAt: string;
}

export interface Assignee {
  id: string;
  telegramId: string;
  firstName: string;
  lastName: string | null;
  username: string | null;
  photoUrl?: string | null;
  accepted: boolean;
  acceptedAt?: string | null;
}

export interface Comment {
  id: string;
  type: 'EXECUTION' | 'MANAGEMENT';
  text: string;
  user: {
    id: string;
    firstName: string;
    lastName: string | null;
  };
  createdAt: string;
}

export const tasksApi = {
  // Получить задачи текущего пользователя
  myTasks: () => request<Task[]>('/tasks/my'),
  
  list: (params: {
    protocolId?: string;
    sectionId?: string;
    assigneeId?: string;
    status?: string;
    overdue?: boolean;
  }) => {
    const query = new URLSearchParams();
    if (params.protocolId) query.set('protocolId', params.protocolId);
    if (params.sectionId) query.set('sectionId', params.sectionId);
    if (params.assigneeId) query.set('assigneeId', params.assigneeId);
    if (params.status) query.set('status', params.status);
    if (params.overdue) query.set('overdue', 'true');
    return request<Task[]>(`/tasks?${query}`);
  },
  
  get: (id: string) => request<TaskDetail>(`/tasks/${id}`),
  
  create: (data: {
    protocolId: string;
    sectionId: string;
    text: string;
    assigneeIds?: string[];
    startDate?: string | null;
    dueDate?: string | null;
    priority?: 'CRITICAL' | 'HIGH' | 'CURRENT';
    status?: string;
  }) =>
    request<Task>('/tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: Partial<{
    text: string;
    assigneeIds: string[];
    startDate: string | null;
    dueDate: string | null;
    priority: 'CRITICAL' | 'HIGH' | 'CURRENT';
    status: string;
  }>) =>
    request<Task>(`/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  addComment: (taskId: string, data: { type: 'EXECUTION' | 'MANAGEMENT'; text: string }) =>
    request<Comment>(`/tasks/${taskId}/comments`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  getComments: (taskId: string, type?: 'EXECUTION' | 'MANAGEMENT') => {
    const query = type ? `?type=${type}` : '';
    return request<Comment[]>(`/tasks/${taskId}/comments${query}`);
  },
  
  delete: (id: string) =>
    request<{ success: boolean }>(`/tasks/${id}`, {
      method: 'DELETE',
    }),

  accept: (taskId: string) =>
    request<{ success: boolean; acceptedAt: string }>(`/tasks/${taskId}/accept`, {
      method: 'POST',
    }),

  approve: (taskId: string) =>
    request<Task>(`/tasks/${taskId}/approve`, {
      method: 'POST',
    }),

  duplicate: (taskId: string) =>
    request<Task>(`/tasks/${taskId}/duplicate`, {
      method: 'POST',
    }),

  getHistory: (taskId: string) =>
    request<Array<{
      id: string;
      action: string;
      beforeData: any;
      afterData: any;
      user: { id: string; firstName: string; lastName: string | null };
      createdAt: string;
    }>>(`/tasks/${taskId}/history`),
};

// ==================== USERS ====================

export interface User {
  id: string;
  telegramId: string;
  firstName: string;
  lastName: string | null;
  username: string | null;
  photoUrl: string | null;
}

// ProjectMember уже определен выше

export interface SystemAdmin extends User {
  isSystemAdmin: boolean;
  createdAt: string;
}

export const usersApi = {
  me: () => request<User & { isSystemAdmin?: boolean }>('/users/me'),
  
  search: (query: string, projectId?: string) => {
    const params = new URLSearchParams({ q: query });
    if (projectId) params.set('projectId', projectId);
    return request<User[]>(`/users/search?${params}`);
  },
  
  // Получить участников проекта
  getProjectMembers: (projectId: string) =>
    request<ProjectMember[]>(`/users/project/${projectId}`),
  
  // Получить подчиненных (для MANAGER - EXECUTOR в проектах, для ADMIN - все)
  getSubordinates: () => request<User[]>('/users/subordinates'),
  
  // Создать нового ответственного
  createAssignee: (data: {
    firstName: string;
    lastName?: string;
    username?: string;
    projectId?: string;
  }) =>
    request<User>('/users/create-assignee', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // Управление системными админами
  getSystemAdmins: () => request<SystemAdmin[]>('/users/system-admins'),
  
  assignSystemAdmin: (userId: string) =>
    request<SystemAdmin>(`/users/system-admins/${userId}`, {
      method: 'POST',
    }),
  
  removeSystemAdmin: (userId: string) =>
    request<SystemAdmin>(`/users/system-admins/${userId}`, {
      method: 'DELETE',
    }),
};

// ==================== KPI ====================

export interface KpiData {
  project: {
    id: string;
    name: string;
    kpiSplitMode: 'EACH_FULL' | 'SPLIT_EQUAL';
  };
  period: {
    startDate: string | null;
    endDate: string | null;
  };
  totals: {
    totalTasks: number;
    completed: number;
    inProgress: number;
    overdue: number;
    paused: number;
  };
  members: KpiMember[];
}

export interface KpiMember {
  userId: string;
  firstName: string;
  lastName: string | null;
  username: string | null;
  assigned: number;
  completed: number;
  completedOnTime: number;
  overdue: number;
  inProgress: number;
  avgCompletionDays: number | null;
  completionRate: number;
  onTimeRate: number;
}

export const kpiApi = {
  get: (params: {
    projectId: string;
    startDate?: string;
    endDate?: string;
    assigneeId?: string;
  }) => {
    const query = new URLSearchParams({ projectId: params.projectId });
    if (params.startDate) query.set('startDate', params.startDate);
    if (params.endDate) query.set('endDate', params.endDate);
    if (params.assigneeId) query.set('assigneeId', params.assigneeId);
    return request<KpiData>(`/kpi?${query}`);
  },
  
  export: (projectId: string, startDate?: string, endDate?: string) =>
    request<{ success: boolean; sheetId: string }>('/kpi/export', {
      method: 'POST',
      body: JSON.stringify({ projectId, startDate, endDate }),
    }),
};

// ==================== SPEECH TO TEXT ====================

export const speechApi = {
  transcribe: async (audioBlob: Blob): Promise<{ text: string }> => {
    const initData = useAuthStore.getState().initData;
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');

    const response = await fetch(`${API_URL}/speech-to-text`, {
      method: 'POST',
      headers: {
        ...(initData && { Authorization: `tma ${initData}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Ошибка распознавания' }));
      throw new ApiError(response.status, error.error || 'Ошибка распознавания');
    }

    return response.json();
  },
};

// ==================== CHECKLISTS ====================

export interface ChecklistTemplate {
  id: string;
  projectId: string | null;
  type: 'REGULATION' | 'EVENT' | 'PERSONAL';
  title: string;
  description: string | null;
  createdById: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  project?: { id: string; name: string } | null;
  createdBy?: { id: string; firstName: string; lastName: string | null };
  rules?: ChecklistItemRule[];
}

export interface ChecklistItemSubtask {
  id: string;
  ruleId: string;
  text: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChecklistItemRule {
  id: string;
  templateId: string;
  text: string;
  recurrence: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'ONCE' | 'NO_DATE';
  scheduleParams: any;
  duePolicy: any;
  requiresApproval: boolean;
  priority: 'CRITICAL' | 'HIGH' | 'CURRENT' | null;
  description: string | null;
  sortOrder: number;
  subtasks?: ChecklistItemSubtask[];
}

export interface ChecklistAssignment {
  id: string;
  templateId: string;
  assigneeId: string;
  activeFrom: string;
  activeTo: string | null;
  status: 'ACTIVE' | 'ARCHIVED';
  approverDailyId: string | null;
  approverWeeklyMonthlyId: string | null;
  approverEventId: string | null;
  template?: ChecklistTemplate;
  assignee?: { id: string; firstName: string; lastName: string | null };
}

export interface ChecklistItemInstance {
  id: string;
  assignmentId: string;
  ruleId: string;
  plannedDate: string;
  dueAt: string;
  status: 'ASSIGNED' | 'IN_PROGRESS' | 'COMPLETED' | 'COMPLETED_PENDING' | 'ACCEPTED' | 'REJECTED';
  completedAt: string | null;
  approverId: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  rejectionComment: string | null;
  assignment?: ChecklistAssignment;
  rule?: ChecklistItemRule;
  approver?: { id: string; firstName: string; lastName: string | null };
  comments?: ChecklistComment[];
}

export interface ChecklistComment {
  id: string;
  instanceId: string;
  userId: string;
  text: string;
  createdAt: string;
  user?: { id: string; firstName: string; lastName: string | null };
}

export interface ChecklistKpi {
  id: string;
  userId: string;
  projectId: string | null;
  periodStart: string;
  periodEnd: string;
  assignedCount: number;
  completedCount: number;
  acceptedCount: number;
  ontimeAcceptedCount: number;
  overdueCount: number;
  rejectedCount: number;
  avgApprovalTime: number | null;
  dailyStreak: number | null;
  acceptanceRate?: number;
  ontimeRate?: number;
  completionRate?: number;
}

export interface ChecklistSettings {
  id: string;
  projectId: string;
  approvalRequiredRegulation: boolean;
  approvalRequiredEvent: boolean;
  approvalRequiredPersonal: boolean;
  kpiCountCompletedAsAccepted: boolean;
  includePersonalInKpi: boolean;
  instanceGenerationHorizon: number;
  workingDays: number[] | null;
  overdueReminderPolicy: any;
  morningDigestTime: string | null;
  notifyOnInstanceAssignment: boolean;
  statusAfterRejection: 'REJECTED' | 'IN_PROGRESS' | 'ASSIGNED' | null;
}

export const checklistsApi = {
  list: (params?: { projectId?: string; type?: string }) => {
    const query = new URLSearchParams();
    if (params?.projectId) query.set('projectId', params.projectId);
    if (params?.type) query.set('type', params.type);
    return request<ChecklistTemplate[]>(`/checklists?${query}`);
  },

  get: (id: string) => request<ChecklistTemplate>(`/checklists/${id}`),

  create: (data: {
    projectId?: string;
    type: 'REGULATION' | 'EVENT' | 'PERSONAL';
    title: string;
    description?: string;
    rules?: Array<{
      text: string;
      recurrence: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'ONCE' | 'NO_DATE';
      scheduleParams?: any;
      duePolicy?: any;
      requiresApproval?: boolean;
      priority?: 'CRITICAL' | 'HIGH' | 'CURRENT';
      description?: string;
      sortOrder?: number;
    }>;
  }) =>
    request<ChecklistTemplate>('/checklists', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<ChecklistTemplate>) =>
    request<ChecklistTemplate>(`/checklists/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<void>(`/checklists/${id}`, {
      method: 'DELETE',
    }),

  addRule: (id: string, data: {
    text: string;
    recurrence: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'ONCE' | 'NO_DATE';
    scheduleParams?: any;
    duePolicy?: any;
    requiresApproval?: boolean;
    priority?: 'CRITICAL' | 'HIGH' | 'CURRENT';
    description?: string;
    sortOrder?: number;
    startDate?: string;
  }) =>
    request<ChecklistItemRule>(`/checklists/${id}/rules`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateRule: (
    templateId: string,
    ruleId: string,
    data: Partial<{
      text: string;
      recurrence: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'ONCE' | 'NO_DATE';
      scheduleParams: any;
      duePolicy: any | null;
      requiresApproval: boolean;
      priority: 'CRITICAL' | 'HIGH' | 'CURRENT' | null;
      description: string | null;
      sortOrder: number;
      startDate: string;
      endDate: string;
    }>
  ) =>
    request<ChecklistItemRule>(`/checklists/${templateId}/rules/${ruleId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteRule: async (templateId: string, ruleId: string) => {
    await request<void>(`/checklists/${templateId}/rules/${ruleId}`, {
      method: 'DELETE',
    });
  },

  sync: (projectId: string, direction: 'to_sheets' | 'from_sheets') =>
    request<{ success: boolean; message: string; instancesCount?: number; updated?: number; errors?: number; total?: number }>('/checklists/sync', {
      method: 'POST',
      body: JSON.stringify({ projectId, direction }),
    }),
};

export const checklistAssignmentsApi = {
  list: (params?: { templateId?: string; assigneeId?: string; status?: string }) => {
    const query = new URLSearchParams();
    if (params?.templateId) query.set('templateId', params.templateId);
    if (params?.assigneeId) query.set('assigneeId', params.assigneeId);
    if (params?.status) query.set('status', params.status);
    const queryString = query.toString();
    return request<ChecklistAssignment[]>(`/checklist-assignments${queryString ? `?${queryString}` : ''}`);
  },

  get: (id: string) => request<ChecklistAssignment>(`/checklist-assignments/${id}`),

  create: (data: {
    templateId: string;
    assigneeIds: string[];
    activeFrom: string;
    activeTo?: string;
    approverDailyId?: string;
    approverWeeklyMonthlyId?: string;
    approverEventId?: string;
  }) =>
    request<ChecklistAssignment[]>('/checklist-assignments', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<ChecklistAssignment>) =>
    request<ChecklistAssignment>(`/checklist-assignments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<void>(`/checklist-assignments/${id}`, {
      method: 'DELETE',
    }),
};

export const checklistInstancesApi = {
  getPlan: (horizon: 'today' | 'tomorrow' | 'week' | 'month') =>
    request<{
      horizon: string;
      startDate: string;
      endDate: string;
      instances: ChecklistItemInstance[];
      grouped: {
        regulation: ChecklistItemInstance[];
        event: ChecklistItemInstance[];
        personal: ChecklistItemInstance[];
      };
    }>(`/checklist-instances/plan?horizon=${horizon}`),

  list: (params?: { assignmentId?: string; status?: string; fromDate?: string; toDate?: string }) => {
    const query = new URLSearchParams();
    if (params?.assignmentId) query.set('assignmentId', params.assignmentId);
    if (params?.status) query.set('status', params.status);
    if (params?.fromDate) query.set('fromDate', params.fromDate);
    if (params?.toDate) query.set('toDate', params.toDate);
    return request<ChecklistItemInstance[]>(`/checklist-instances?${query}`);
  },

  get: (id: string) => request<ChecklistItemInstance>(`/checklist-instances/${id}`),

  updateStatus: (id: string, status: 'ASSIGNED' | 'IN_PROGRESS' | 'COMPLETED' | 'COMPLETED_PENDING') =>
    request<ChecklistItemInstance>(`/checklist-instances/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  addComment: (id: string, text: string) =>
    request<ChecklistComment>(`/checklist-instances/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
};

export const checklistApprovalsApi = {
  getPending: () => request<ChecklistItemInstance[]>('/checklist-approvals/pending'),

  approve: (id: string, comment?: string) =>
    request<ChecklistItemInstance>(`/checklist-approvals/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    }),

  reject: (id: string, data: { comment?: string; rejectionReasonId?: string }) =>
    request<ChecklistItemInstance>(`/checklist-approvals/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  addComment: (id: string, text: string) =>
    request<ChecklistComment>(`/checklist-approvals/${id}/comment`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
};

export const checklistKpiApi = {
  get: (params: {
    userId?: string;
    projectId?: string;
    startDate: string;
    endDate: string;
  }) => {
    const query = new URLSearchParams();
    if (params.userId) query.set('userId', params.userId);
    if (params.projectId) query.set('projectId', params.projectId);
    query.set('startDate', params.startDate);
    query.set('endDate', params.endDate);
    return request<ChecklistKpi>(`/checklist-kpi?${query}`);
  },

  getManager: (params: { projectId: string; startDate?: string; endDate?: string }) => {
    const query = new URLSearchParams();
    query.set('projectId', params.projectId);
    if (params.startDate) query.set('startDate', params.startDate);
    if (params.endDate) query.set('endDate', params.endDate);
    return request<{
      pendingCount: number;
      processedCount: number;
      acceptedCount: number;
      rejectedCount: number;
      rejectionRate: number;
      avgApprovalTime: number | null;
    }>(`/checklist-kpi/manager?${query}`);
  },
};

export const checklistSettingsApi = {
  get: (projectId: string) => request<ChecklistSettings>(`/checklist-settings/${projectId}`),

  update: (projectId: string, data: Partial<ChecklistSettings>) =>
    request<ChecklistSettings>(`/checklist-settings/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
};

export interface RejectionReason {
  id: string;
  projectId: string | null;
  text: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export const checklistSubtasksApi = {
  list: (ruleId: string) => request<ChecklistItemSubtask[]>(`/checklist-subtasks/rule/${ruleId}`),
  
  create: (ruleId: string, data: { text: string; sortOrder?: number }) =>
    request<ChecklistItemSubtask>(`/checklist-subtasks/rule/${ruleId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: string, data: { text?: string; sortOrder?: number }) =>
    request<ChecklistItemSubtask>(`/checklist-subtasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) =>
    request<void>(`/checklist-subtasks/${id}`, {
      method: 'DELETE',
    }),
};

export const rejectionReasonsApi = {
  list: (params?: { projectId?: string }) => {
    const query = new URLSearchParams();
    if (params?.projectId) query.set('projectId', params.projectId);
    return request<RejectionReason[]>(`/rejection-reasons?${query}`);
  },

  create: (data: {
    projectId?: string;
    text: string;
    sortOrder?: number;
  }) =>
    request<RejectionReason>('/rejection-reasons', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<RejectionReason>) =>
    request<RejectionReason>(`/rejection-reasons/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<void>(`/rejection-reasons/${id}`, {
      method: 'DELETE',
    }),
};

